import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  type KeyboardEvent,
  type RefObject,
} from "react";
import { Maximize2 } from "lucide-react";
import { useSelector } from "react-redux";
import { matchPath, useLocation, useNavigate, useSearchParams } from "react-router-dom";

import {
  parseContextRef,
  targetKey,
  usePageContext,
  useReloadDefer,
  type AgentState,
  type ContextRef,
} from "@/agent";
import { ASSISTANT_PATH, openOnChat, type ChatPick } from "@/agent/assistant/handoff";
import { AgentRuntime } from "@/agent/aui";
import { AgentChat, NoticeProvider, PHONE_QUERY } from "@/agent/chat";
import { canUseAgent } from "@/auth/capabilities";
import ErrorBoundary from "@/components/error/ErrorBoundary";
import { Button } from "@/components/ui/button";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { cn } from "@/lib/utils";

import { cardScroller, followBottom } from "@/agent/choices/followCard";

import { LipBar } from "./LipBar";
import { LipAddDocument } from "./LipAddDocument";
import { LipDropCard } from "./LipDropCard";
import { LipHandle } from "./LipHandle";
import { LipPreview } from "./LipPreview";
import { clearLipChoice, openLipOn, setLipOpen, useLipChoice, useLipState } from "./lipStore";
import { useCloseOnOutside } from "./useCloseOnOutside";
import { usePageFileDrag } from "./usePageFileDrag";
import { useUnseen } from "./useUnseen";

/** Pages that show the assistant themselves (react-router patterns); the lip steps aside there. */
export const LIP_HIDDEN_ROUTES: readonly string[] = ["/assistant/*"];

/**
 * The lip shows on application pages only (design 19.t); General chat lives on /assistant. True
 * brings the lip back on every other page, as the General chat or that page's context.
 */
export const LIP_ON_GENERAL = false;

/** Whether a page showing `contextRef` gets the lip. */
export function lipShownFor(contextRef: ContextRef | null, onGeneral = LIP_ON_GENERAL): boolean {
  return onGeneral || parseContextRef(contextRef)?.type === "application";
}

// DEV only: window.__assistantLip seeds example threads for screenshots (no backend, no model).
const LipFixtures = import.meta.env.DEV ? lazy(() => import("./LipFixtures")) : null;
const LIP_BODY_ID = "assistant-lip-body";
/** `?choice=<ref>` on an application's link (an approval made elsewhere) opens the lip on its card. */
export const CHOICE_PARAM = "choice";
const FOCUS_TRIES = 30;
const FOCUS_INTERVAL_MS = 100;

/**
 * Opens the lip on the choice a link names, then brings its card into view and focuses it once the
 * thread shows it. A choice the thread doesn't show (unknown, already decided) just opens the lip.
 */
function useLinkedChoice(lip: RefObject<HTMLElement>, threadId: number | null) {
  const [params, setParams] = useSearchParams();
  const linked = params.get(CHOICE_PARAM);
  useEffect(() => {
    if (!linked) return;
    openLipOn(linked);
    // Taken off the address, so a reload or a later visit does not reopen it.
    setParams(
      (current) => {
        current.delete(CHOICE_PARAM);
        return current;
      },
      { replace: true, preventScrollReset: true }
    );
  }, [linked, setParams]);

  const wanted = useLipChoice();
  const loaded = useSelector(
    (state: { agent: AgentState }) => threadId != null && threadId in state.agent.threads
  );
  // Outlives the search below (which ends once the frame is focused): the card keeps being
  // followed while its frame sizes itself, and stops when the lip goes.
  const following = useRef<(() => void) | null>(null);
  useEffect(() => () => following.current?.(), []);
  useEffect(() => {
    if (!wanted || !loaded) return;
    // The card can land just after the snapshot (a live event), and its frame mounts once the view
    // has loaded: both are looked for over a short window, then the lip is left simply open.
    let tries = 0;
    let shown = false;
    const timer = window.setInterval(() => {
      const card = lip.current?.querySelector<HTMLElement>(
        `[data-choice-ref="${CSS.escape(wanted)}"]`
      );
      if (card && !shown) {
        // Its bottom edge, where its buttons are, followed while its frame sizes itself: the
        // same way the strip brings a card to its buttons.
        const scroller = cardScroller(card);
        following.current?.();
        following.current = scroller ? followBottom(card, scroller) : null;
        if (!scroller) card.scrollIntoView({ block: "end" });
        shown = true;
      }
      // Into the approval's frame, so its buttons are the next keys.
      const frame = card?.querySelector("iframe");
      frame?.focus({ preventScroll: true });
      if (frame || ++tries >= FOCUS_TRIES) {
        window.clearInterval(timer);
        clearLipChoice();
      }
    }, FOCUS_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [lip, wanted, loaded]);
}

export function Lip({ contextRef }: { contextRef: ContextRef | null }) {
  const navigate = useNavigate();
  const target = useMemo<ChatPick>(
    () => (contextRef ? { kind: "context", ref: contextRef } : { kind: "general" }),
    [contextRef]
  );
  const key = targetKey(target);
  const threadId = useSelector(
    (state: { agent: AgentState }) => state.agent.targets[key]?.threadId ?? null
  );
  // A file or document dragged over the page outlines the lip; over the lip, it rises to a drop
  // area, and a drop there opens it. An open lip stays as it is: its chat has its own drop area.
  const state = useLipState();
  const open = state === "open";
  const lip = useRef<HTMLElement>(null);
  usePageFileDrag(lip, threadId != null);
  const unseen = useUnseen(key, open);
  const bar = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);

  // Mounted in every state so opening never paints an empty card; inert until open. Inert alone
  // does not stop Chromium sending the mouse to a sandboxed frame (an approval card) scrolled
  // under the bar, so the closed body also takes no pointer events.
  useEffect(() => {
    if (body.current) body.current.inert = !open;
  }, [open]);
  useLinkedChoice(lip, threadId);

  /**
   * Closes the card; the focus it held goes back to the bar. After a press, with no ring: a text
   * input always shows one, and Chrome hands it on to what script focuses next.
   */
  const closeLip = (pressed = false) => {
    setLipOpen(false);
    if (pressed && document.activeElement instanceof HTMLElement) document.activeElement.blur();
    bar.current?.focus({ focusVisible: !pressed });
  };
  useCloseOnOutside(lip, open, () => closeLip(true));

  /**
   * A page's "Add to chat" (the keyboard's way in) opened the lip: its composer takes the focus,
   * so the question comes next and Escape closes the lip. After this render's effects, which make
   * the body focusable. On a phone the bar takes it instead (the page's button is hidden under the
   * lip): a textarea there would raise the keyboard over the lip, a button raises nothing.
   */
  const focusLip = useCallback(() => {
    const phone = window.matchMedia(PHONE_QUERY).matches;
    requestAnimationFrame(() => {
      if (phone) {
        bar.current?.focus({ preventScroll: true, focusVisible: false });
        return;
      }
      body.current
        ?.querySelector<HTMLTextAreaElement>('textarea[aria-label="Message"]')
        ?.focus({ preventScroll: true });
    });
  }, []);

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || event.defaultPrevented || !open) return;
    closeLip();
  };

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-0 z-lip flex justify-center transition-[left] duration-300 ease-spring motion-reduce:transition-none sm:left-[var(--drawer-width)] sm:px-10">
      <section
        ref={lip}
        data-assistant-lip=""
        data-state={state}
        aria-label="Steddy Assistant"
        onKeyDown={onKeyDown}
        className={cn(
          "group/lip pointer-events-auto relative flex h-dvh w-full max-w-[1320px] flex-col bg-card text-card-foreground shadow-lip will-change-transform",
          "translate-y-[calc(100%_-_var(--assistant-lip-peek))] transition-[transform,height] duration-travel ease-spring data-[state=open]:translate-y-0 data-[state=over]:translate-y-[calc(100%_-_var(--assistant-lip-drop))]",
          // Hover height: a desktop hover, a drag over the page (where to drop), and keyboard focus
          // on the bar or the preview line (which sits below the fold at peek).
          "[@media(hover:hover)_and_(pointer:fine)]:data-[state=peek]:hover:translate-y-[calc(100%_-_var(--assistant-lip-hover))]",
          "data-[state=page-drag]:translate-y-[calc(100%_-_var(--assistant-lip-hover))] data-[state=peek]:has-[[data-lip-strip]:focus-visible]:translate-y-[calc(100%_-_var(--assistant-lip-hover))]",
          "data-[state=peek]:has-[[data-lip-bar]:focus-visible]:translate-y-[calc(100%_-_var(--assistant-lip-hover))]",
          // A handle drag holds the lip up at the dragged height, following the pointer.
          "data-[dragging]:!translate-y-0 data-[dragging]:transition-none",
          "max-sm:data-[state=open]:shadow-none sm:h-[var(--assistant-lip-size,min(var(--assistant-lip-height),72vh))] sm:rounded-t-assistant sm:border sm:border-b-0 sm:border-border sm:data-[state=over]:border-primary/50"
        )}
      >
        {state === "page-drag" && (
          <div className="pointer-events-none absolute inset-1 z-10 border border-dashed border-primary sm:rounded-t-[calc(var(--assistant-radius)_-_4px)]" />
        )}
        <LipBar
          ref={bar}
          targetKey={key}
          open={open}
          unseen={unseen}
          controls={LIP_BODY_ID}
          onToggle={() => setLipOpen(!open)}
        >
          {open && (
            <Button
              variant="ghost"
              size="icon"
              aria-label="Open full page"
              className="h-7 w-7 shrink-0 text-muted-foreground max-sm:h-10 max-sm:w-10"
              onClick={() => {
                setLipOpen(false);
                navigate(ASSISTANT_PATH, { state: openOnChat(target) });
              }}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </LipBar>
        {(state === "peek" || state === "page-drag") && <LipPreview targetKey={key} />}
        {/* After the bar and the reply line in the tab order; drawn on the top edge. */}
        <LipHandle lip={lip} open={open} />
        {/* The lip sits outside the page's error boundary: a chat that throws empties the card,
            never the app. Another context or thread mounts a fresh one. */}
        <ErrorBoundary key={`${key}:${threadId}`} fallback={null}>
          <AgentRuntime target={target} view="card">
            <NoticeProvider>
              {state === "over" && <LipDropCard />}
              <LipAddDocument onAdding={focusLip} />
              <div
                ref={body}
                id={LIP_BODY_ID}
                className="pointer-events-none flex min-h-0 flex-1 flex-col opacity-0 transition-opacity duration-fade ease-linear group-data-[state=open]/lip:pointer-events-auto group-data-[dragging]/lip:opacity-100 group-data-[state=open]/lip:opacity-100"
              >
                <AgentChat className="flex-1" />
              </div>
            </NoticeProvider>
          </AgentRuntime>
        </ErrorBoundary>
      </section>
      {LipFixtures && (
        <Suspense fallback={null}>
          <LipFixtures targetKey={key} />
        </Suspense>
      )}
    </div>
  );
}

/**
 * The assistant's card docked at the bottom of application pages (design 19, concept 1A; 19.t): a
 * 32px bar at peek, the reply preview on desktop hover, an outline while a file is dragged over
 * the page and a drop area while it is over the lip, the thread when open (full screen on a
 * phone). Mount it once in the app shell, outside anything keyed by route, so navigating never
 * remounts it.
 */
export function AssistantLip() {
  const { user } = useCurrentUser();
  return canUseAgent(user) ? <AssistantShell /> : null;
}

/** Focus reporting and the reload hold run on every page; the lip renders only where it shows. */
function AssistantShell() {
  const contextRef = usePageContext();
  useReloadDefer();
  const { pathname } = useLocation();
  const hidden = LIP_HIDDEN_ROUTES.some((route) => matchPath(route, pathname));
  return hidden || !lipShownFor(contextRef) ? null : <Lip contextRef={contextRef} />;
}
